import { oauthService } from "./oauth";

const API_BASE_URL =
  process.env.REACT_APP_API_SERVER_URL || "http://localhost:8000";

const getAuthHeaders = () => ({
  Authorization: `Bearer ${oauthService.getToken()}`,
  "Content-Type": "application/json",
});

export const getRbacConfig = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/admin/rbac-config`, {
      headers: getAuthHeaders(),
    });
    if (response.status === 401) {
      throw new Error("Unauthorized");
    }
    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData?.message || `Request failed (${response.status})`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching RBAC config:", error);
    throw error;
  }
};

export const updateRbacConfig = async (payload) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/admin/rbac-config`, {
      method: "PUT",
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    if (response.status === 401) {
      throw new Error("Unauthorized");
    }
    if (response.status === 403) {
      throw new Error("You don't have permission to manage RBAC");
    }
    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData?.message || `Request failed (${response.status})`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error updating RBAC config:", error);
    throw error;
  }
};

export const getAdminStats = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/admin/stats`, {
      headers: getAuthHeaders(),
    });

    if (response.status === 401) {
      throw new Error("Unauthorized - please log in again");
    }

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      const msg = errData?.message || `Request failed (${response.status})`;
      throw new Error(msg);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching admin stats:", error);
    throw error;
  }
};

export const getWeeklyChatFeedback = async (days = 7) => {
  const response = await fetch(
    `${API_BASE_URL}/api/admin/chat-feedback?days=${encodeURIComponent(days)}`,
    { headers: getAuthHeaders() },
  );
  if (response.status === 401) {
    throw new Error("Unauthorized - please log in again");
  }
  if (!response.ok) {
    const errData = await response.json().catch(() => ({}));
    throw new Error(errData?.message || `Request failed (${response.status})`);
  }
  return await response.json();
};

function browserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

export const getMonthlyAiUsage = async () => {
  const zone = encodeURIComponent(browserTimeZone());
  const response = await fetch(`${API_BASE_URL}/api/admin/ai-usage?timezone=${zone}`, {
    headers: getAuthHeaders(),
  });
  if (response.status === 401) {
    throw new Error("Unauthorized");
  }
  if (!response.ok) {
    const errData = await response.json().catch(() => ({}));
    throw new Error(errData?.message || `Request failed (${response.status})`);
  }
  return await response.json();
};

export const getAdminUsers = async (query = "", options = {}) => {
  try {
    const url = new URL(`${API_BASE_URL}/api/admin/users`);
    if (query) url.searchParams.set("q", query);
    const { limit, offset } = options || {};
    if (offset !== undefined && offset !== null) {
      url.searchParams.set("offset", String(offset));
      if (limit != null) url.searchParams.set("limit", String(limit));
    }
    const response = await fetch(url.toString(), {
      headers: getAuthHeaders(),
    });

    if (response.status === 401) {
      throw new Error("Unauthorized - please log in again");
    }
    if (response.status === 403) {
      throw new Error("You don't have permission to manage RBAC");
    }

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      const msg = errData?.message || `Request failed (${response.status})`;
      throw new Error(msg);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching admin users:", error);
    throw error;
  }
};

export const updateUserRole = async (userId, role) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/admin/users/${userId}/role`,
      {
        method: "PATCH",
        headers: getAuthHeaders(),
        body: JSON.stringify({ role }),
      }
    );

    if (response.status === 401) {
      throw new Error("Unauthorized - please log in again");
    }
    if (response.status === 403) {
      throw new Error("You don't have permission to manage RBAC");
    }

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      const msg = errData?.message || `Request failed (${response.status})`;
      throw new Error(msg);
    }

    return await response.json();
  } catch (error) {
    console.error("Error updating user role:", error);
    throw error;
  }
};

export const getAdminDocumentTypes = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/admin/document-types`, {
      headers: getAuthHeaders(),
    });

    if (response.status === 401) {
      throw new Error("Unauthorized - please log in again");
    }
    if (response.status === 403) {
      throw new Error("You don't have permission to manage approval workflows");
    }

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData?.message || `Request failed (${response.status})`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching admin document types:", error);
    throw error;
  }
};

export const updateAdminDocumentType = async (typeId, ownerUserId) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/admin/document-types/${typeId}`,
      {
        method: "PATCH",
        headers: getAuthHeaders(),
        body: JSON.stringify({ owner_user_id: ownerUserId }),
      }
    );

    if (response.status === 401) {
      throw new Error("Unauthorized - please log in again");
    }
    if (response.status === 403) {
      throw new Error("You don't have permission to manage approval workflows");
    }

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData?.message || `Request failed (${response.status})`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error updating document type:", error);
    throw error;
  }
};

export const getApprovalWorkflows = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/admin/approval-workflow`, {
      headers: getAuthHeaders(),
    });

    if (response.status === 401) {
      throw new Error("Unauthorized - please log in again");
    }
    if (response.status === 403) {
      throw new Error("You don't have permission to manage approval workflows");
    }

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData?.message || `Request failed (${response.status})`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching approval workflows:", error);
    throw error;
  }
};

export const updateApprovalWorkflow = async (typeId, steps) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/admin/approval-workflow/${typeId}`,
      {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify({ steps }),
      }
    );

    if (response.status === 401) {
      throw new Error("Unauthorized - please log in again");
    }
    if (response.status === 403) {
      throw new Error("You don't have permission to manage approval workflows");
    }

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData?.message || `Request failed (${response.status})`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error updating approval workflow:", error);
    throw error;
  }
};
